import React, { useState, useEffect, useContext, useCallback } from "react";
import { Button, Table, Space, Dropdown, notification, Modal } from "antd";
import { DeleteOutlined, EditOutlined, DownOutlined } from "@ant-design/icons";
import UserForm from "../components/Forms/UserForm";
import axios from "axios";
import "moment-timezone";
import GlobalContext from "../utils/GlobalContext";
import { v4 as uuidv4 } from "uuid";
import LoadingModal from "../utils/LoadingModal";

const PAGE_SIZE = 15;

const UserTable = () => {
  const globals = useContext(GlobalContext);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: PAGE_SIZE,
  });

  const columns = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 300,
    },
    {
      title: "Nombre",
      dataIndex: "firstName",
      key: "firstname",
    },
    {
      title: "Apellido",
      dataIndex: "lastName",
      key: "lastname",
    },
    {
      title: "Rol",
      dataIndex: "role",
      key: "role",
      render: (_, record) => (
        <Space size="middle">
          {record.role === "admin" ? "admin" : "usuario"}
        </Space>
      ),
    },
    {
      title: "Tipo",
      dataIndex: "type",
      key: "type",
      render: (_, record) => (
        record.type.toLowerCase()
      ),
    },
    {
      title: "Opciones",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <Space size="middle">
          <Dropdown
            trigger={["click"]}
            menu={{
              items:[
                {
                  key: '1',
                  label: 'Editar',
                  icon:<EditOutlined />,
                  onClick: () => handleActionClick("edit", record)
                },
                {
                  key: '2',
                  label: 'Eliminar',
                  icon:<DeleteOutlined />,
                  onClick: () => handleActionClick("delete", record)
                },
              ],
            }}
          >
            <Button icon={<DownOutlined />} shape="circle" />
          </Dropdown>
        </Space>
      ),
    },
  ];

  const fetchData = useCallback(
    async (newPagination, search) => {
      let page = 1;
      let pageSize = PAGE_SIZE;
      if (newPagination) {
        page = newPagination.current;
        pageSize = newPagination.pageSize;
      }
      try {
        setLoading(true);
        await new Promise((resolve) => setTimeout(resolve, 100));
        const response = await axios.get(
          `${globals.SERVICE_API_DB}/v1/list-users`,
          {
            headers: {
              "x-token": globals.token,
            },
            params: {
              page,
              pageSize,
            },
          }
        );
        setData(response?.data?.data.users);
        if (newPagination) {
          setPagination({
            ...newPagination,
            total: response?.data?.data.pagination.totalResults,
          });
        } else {
          setPagination({
            ...pagination,
            total: response?.data?.data.pagination.totalResults,
          }
          );
        }
        notification.success({
          message: "Información obtenida exitosamente!",
          placement: "bottomRight",
          duration: 2,
        });
      } catch (error) {
        console.error("Error fetching data:", error);
        notification.error({
          message: "Error obteniendo información!",
          placement: "bottomRight",
        });
      }
      setLoading(false);
    },
    // eslint-disable-next-line
    [globals.SERVICE_SERVICE_API_DB]
  );

  useEffect(() => {
    fetchData(null);
  }, [fetchData]);

  const handleActionClick = async (key, record) => {
    if (key === "edit") {
      setIsEdit(true);
      setCurrentUser(record);
      setShowUserForm(true);
    }
    else if (key === "delete") {
      setCurrentUser(record);
      Modal.confirm({
        title: 'Eliminar usuario externo',
        content: '¿Está seguro de eliminar este usuario?',
        okText: 'Eliminar',
        cancelText: 'Cancelar',
        onOk() {
          deleteUser(record);
        },
        
      });
    }
  };

  async function handleTableChange(newPagination) {
    fetchData(newPagination);
  }

  const saveUserForm = async ({status, newUser}) => {
    setShowUserForm(false);
    if (isEdit) {
      setIsEdit(false);
      notification.success({
        message: "Usuario actualizado exitosamente!",
        placement: "bottomRight",
        duration: 2,
      });
      return;
    }
    if(status === "created"){
      setData([...data, newUser]);
    }

    notification.success({
      message: "Usuario creado exitosamente!",
      placement: "bottomRight",
      duration: 2,
    });
  };

  const closeUserForm = () => {
    setShowUserForm(false);
    setIsEdit(false);
    setCurrentUser(null);
  };

  const deleteUser = async (currentUser) => {
    if (!currentUser) {
      return;
    }
    try{
      await axios.delete(`${globals.SERVICE_API_DB}/v1/delete-user/${currentUser.id}`,
      {
        headers: {
          "x-token": globals.token,
        },
      });
      setData(data.filter((item) => item.id !== currentUser.id));
      setCurrentUser(null);
      notification.success({
        message: "Usuario eliminado exitosamente!",
        placement: "bottomRight",
        duration: 2,
      });
    }
    catch(err){
      console.log(err.response.data);
      notification.error({
        message: "Error al eliminar el usuario!",
        placement: "bottomRight",
      });
      return;
    }
  };

  const handleRefresh = () => {
    fetchData(null);
  };

  return (
    <div>
      <div
        style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}
      >
        <h2 style={{ marginRight: "16px" }}>Usuarios Externos</h2>
        <div style={{ flexGrow: 1, borderBottom: "1px solid #ccc" }} />
        <Button
          onClick={() => {
            setIsEdit(false);
            setShowUserForm(true);
          }}
          style={{ marginRight: "8px" }}
        >
          Crear
        </Button>
        <Button
          onClick={handleRefresh}
          disabled={loading}
          style={{ marginRight: "8px" }}
        >
          Refrescar
        </Button>
      </div>
      <div
        className="site-layout-background h-auto"
        style={{
          padding: 10,
          display: "flex",
          flexDirection: "column",
          margin: 0,
          height: "auto",
          minHeight: "350px",
          overflow: "auto",
          backgroundColor: "#ffffff",
        }}
      >
        <Table
          rowKey={(r) => uuidv4()}
          columns={columns}
          dataSource={data}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ y: "max-content" }}
          size="small"
        />
      </div>
      <LoadingModal open={loading} />

      <UserForm
        open={showUserForm}
        onFinish={saveUserForm}
        onCancel={closeUserForm}
        isEdit={isEdit}
        currentUser={currentUser}
      />
    </div>
  );
};

export default UserTable;
